import { BaseSyntheticEvent, useEffect, useState } from 'react';

import { NextPage } from 'next';

import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import useReloadHeaderBidding from '@adverts/hooks/useReloadHeaderBidding';
import useSasicAttributes from '@adverts/hooks/useSasicAttributes';
import useGa from '@analytics/hooks/useGa';
import useGaPage from '@analytics/hooks/useGaPage';
import useTimetablesStore from '@calendar/store/useTimetablesStore';
import CreateEventComponent from '@event/components/CreateEvent';
import EventSummary from '@event/components/EventSummary';
import SubmitEvent from '@event/components/SubmitEvent';
import useEventMutation from '@event/hooks/useEventMutation';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { useTutorialStore } from '@event/store/useTutorialStore';
import { CreateEvent } from '@event/types';
import Footer from '@footer/components/Footer';
import LeavePromptModal from '@modal/components/LeavePromptModal';
import Notification from '@notification/components/Notification';
import usePopupStore from '@popup/stores/usePopupStore';
import LayoutInnerPageWrapper from '@ui/components/LayoutInnerPageWrapper';
import { getRandomInt } from '@utils/getRandomInt';
import useClientStore from '@utils/hooks/useClientStore';

const CreateEventPage: NextPage = () => {
  const { timetables, setTimetables } = useTimetablesStore();
  const { register, setValue, handleSubmit, formState } =
    useForm<CreateEvent>();

  const [photoId, setPhotoId] = useState(1);

  const { push } = useRouter();
  const { mutate: createEvent, isLoading, isSuccess } = useEventMutation();

  const { selectedEventType } = useEventTypesStore();
  const { popupState, setPopupState } = usePopupStore();

  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  const logToGa = useGa();
  const logGaPage = useGaPage();

  useEffect(() => {
    logGaPage('event signboard');
  }, [logGaPage]);

  useReloadHeaderBidding();
  useSasicAttributes({
    keyword: ['event'],
  });

  const {
    isEventImageSelectorOpen,
    isTimeLengthSlotSelectorOpen,
    isEventSummaryShown,
    isSubmitEventShown,
    isEventTimeSlotsModalOpen,
    isLeavePromptModalOpen,
  } = popupState;

  const shouldShowEventSummary =
    !isEventImageSelectorOpen &&
    !isTimeLengthSlotSelectorOpen &&
    !isEventSummaryShown &&
    !isSubmitEventShown &&
    !isEventTimeSlotsModalOpen &&
    !isLeavePromptModalOpen &&
    timetables.length > 0;

  useEffect(() => {
    setTimetables([]);
    setPhotoId(getRandomInt(1, 30));
  }, [setTimetables]);

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    if (timetables.length !== 0 || formState.isDirty) {
      window.addEventListener('beforeunload', handler);
    }

    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [formState.isDirty, timetables.length]);

  useEffect(() => {
    if (shouldShowEventSummary) {
      setPopupState({ isEventSummaryShown: true });
    }
  }, [setPopupState, shouldShowEventSummary]);

  useEffect(() => {
    if (timetables.length === 0) {
      setPopupState({ isSubmitEventShown: false });
      setPopupState({ isEventSummaryShown: false });
    }
  }, [setPopupState, timetables.length]);

  const handleEventSummaryClick = () => {
    setPopupState({ isSubmitEventShown: !isSubmitEventShown });

    logToGa({
      event: 'button_click',
      button: {
        text: 'Vytvořit událost s vybranými časy',
        type: 'share event times',
        phase: 'planning',
      },
    });
  };

  const onSubmit = (data: CreateEvent) => {
    createEvent(
      {
        ...data,
        type: selectedEventType?.typeEnumValue || 'PUB',
        photoId,
        timetables,
      },
      {
        onSuccess: (response) => {
          logToGa({
            event: 'event_create',
            meeting: {
              type: selectedEventType?.name,
              title: data.name,
            },
          });
          push(
            {
              pathname: `/e/${response.slug}`,
              query: {
                isCreator: true,
              },
            },
            `/e/${response.slug}`,
          );
        },
        onError: () =>
          toast(
            <Notification
              type="error"
              text="Omlouváme se, ale akci nelze vytvořit. Nebojte, vše je uložené. Zkontrolujte připojení a zkuste to znovu."
            />,
          ),
      },
    );

    logToGa({
      event: 'button_click',
      button: {
        text: 'Vytvořit akci',
        type: 'share event',
        phase: 'planning',
      },
    });
  };

  const validateAndSubmitForm = (
    data: CreateEvent,
    event?: BaseSyntheticEvent,
  ) => {
    if (!data.name) {
      event?.preventDefault();

      setPopupState({ isSubmitEventShown: false });
      toast(
        <Notification type="error" text="Vyplňte prosím název události." />,
      );

      return;
    }

    onSubmit(data);
  };

  return (
    <form className="relative w-full">
      <LayoutInnerPageWrapper>
        <CreateEventComponent
          register={register}
          setValue={setValue}
          setPhotoId={setPhotoId}
          photoId={photoId}
          formState={formState}
        />
        <Footer className="mt-8" />
      </LayoutInnerPageWrapper>
      {isLeavePromptModalOpen && (
        <LeavePromptModal
          onClose={() => setPopupState({ isLeavePromptModalOpen: false })}
        />
      )}
      <EventSummary
        isShown={isEventSummaryShown}
        timetables={timetables}
        onClick={handleEventSummaryClick}
      />
      <SubmitEvent
        isShown={isSubmitEventShown}
        isSubmitting={isLoading || isSuccess}
        close={() => setPopupState({ isSubmitEventShown: false })}
        onSubmit={handleSubmit(async (data, e) => {
          validateAndSubmitForm(data, e);
        })}
      />
      {tutorialStore?.isTutorialActive && (
        <div className="fixed inset-0 z-10 bg-brand-gray-10060" />
      )}
    </form>
  );
};

export default CreateEventPage;
